import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils.js';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../../Components/ui/collapsible.js';
import { ChevronDown } from 'lucide-react';
import { SearchQueryPill } from './SearchQueryPill.js';
import { ChainOfThoughtWidget } from '@brandclub/bc-widgets';

/** Custom search icon SVG with customizable size and color */
const SearchIcon = ({ className, size = 14, color }) => (jsxs("svg", Object.assign({ xmlns: "http://www.w3.org/2000/svg", width: size, height: size, viewBox: "0 0 14 14", fill: "none", className: className, style: color ? { color } : undefined }, { children: [jsx("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.88248 0C9.13129 0 11.765 2.63362 11.765 5.88234C11.765 7.22364 11.316 8.46008 10.5602 9.44961L13.1039 11.9944C13.4102 12.3007 13.4102 12.7973 13.1039 13.1036C12.8212 13.3863 12.3763 13.4081 12.0686 13.1689L11.9947 13.1036L9.44983 10.56C8.46027 11.3158 7.2238 11.7647 5.88248 11.7647C2.63367 11.7647 0 9.1311 0 5.88237C0 2.63364 2.63368 2.7351e-05 5.88248 2.7351e-05V0Z", fill: "currentColor" }), jsx("path", { d: "M5.8927 2.4707C7.777 2.47088 9.30481 3.99847 9.30481 5.88281C9.30463 7.767 7.77689 9.29474 5.8927 9.29492C4.00836 9.29492 2.48077 7.76711 2.48059 5.88281C2.48059 3.99836 4.00825 2.4707 5.8927 2.4707Z", className: "tw:fill-background tw:stroke-background", strokeWidth: "1.33333" })] })));
/**
 * Inner component that renders the collapsible section with styling from widget state.
 */
const ProductSearchQueriesSectionInner = ({ previewSearchQueries, previewQueryProducts, isStreaming, hasContent, isLastMessage = false, styles, resolveColor, }) => {
    // Track which query products have been shown for staggered animation
    const [animatedQueryIndices, setAnimatedQueryIndices] = useState(new Set());
    const [lastProcessedLength, setLastProcessedLength] = useState(0);
    const [hasEverStreamed, setHasEverStreamed] = useState(false);
    const [isOpen, setIsOpen] = useState(styles.defaultExpanded);
    // Track if user has manually interacted with the collapse toggle
    const userHasInteracted = useRef(false);
    // Handle user toggle - marks that user has taken control
    const handleOpenChange = (open) => {
        userHasInteracted.current = true;
        setIsOpen(open);
    };
    // Track if this component has ever been in streaming mode
    useEffect(() => {
        if (isStreaming) {
            setHasEverStreamed(true);
        }
    }, [isStreaming]);
    // Only animate if actively streaming (not loading existing data) and animations are enabled
    useEffect(() => {
        if (!isStreaming || !styles.animationsEnabled) {
            // If not streaming or animations disabled, show all immediately without animation
            setAnimatedQueryIndices(new Set());
            return;
        }
        // Track new query products for animation
        if (previewQueryProducts.length > lastProcessedLength) {
            const newProducts = previewQueryProducts.slice(lastProcessedLength);
            newProducts.forEach((queryData) => {
                // Mark as animating, then remove after animation completes
                setAnimatedQueryIndices((prev) => new Set([...prev, queryData.queryIndex]));
                setTimeout(() => {
                    setAnimatedQueryIndices((prev) => {
                        const next = new Set(prev);
                        next.delete(queryData.queryIndex);
                        return next;
                    });
                }, styles.productAnimationDuration + 100); // Animation duration + buffer
            });
            setLastProcessedLength(previewQueryProducts.length);
        }
    }, [
        previewQueryProducts.length,
        isStreaming,
        lastProcessedLength,
        styles.animationsEnabled,
        styles.productAnimationDuration,
    ]);
    // Check if all queries have products loaded
    const allQueriesHaveProducts = previewSearchQueries.length > 0 &&
        previewSearchQueries.every((query) => previewQueryProducts.some((qp) => qp.query === query));
    // UX Logic for collapse behavior:
    // 1. Start OPEN during streaming so user sees search happening
    // 2. Auto-collapse ONLY when: all products loaded + content streaming + user hasn't manually interacted
    // 3. Start COLLAPSED for historical messages (loaded from existing conversation)
    // 4. Respect user's manual toggle - once they interact, don't auto-change
    useEffect(() => {
        // Auto-collapse after a delay when all products have arrived and content is streaming
        // Only if user hasn't manually interacted with the toggle and autoCollapse is enabled
        if (styles.autoCollapse &&
            isStreaming &&
            isLastMessage &&
            allQueriesHaveProducts &&
            hasContent &&
            !userHasInteracted.current) {
            const timer = setTimeout(() => {
                setIsOpen(false);
            }, styles.autoCollapseDelay);
            return () => clearTimeout(timer);
        }
    }, [isStreaming, isLastMessage, allQueriesHaveProducts, hasContent, styles.autoCollapse, styles.autoCollapseDelay]);
    // Start collapsed if loading from existing conversation (never streamed)
    // This runs once on mount for historical messages
    // Important: Only collapse if NOT currently streaming (historical data)
    useEffect(() => {
        if (!isStreaming && !hasEverStreamed && previewSearchQueries.length > 0) {
            setIsOpen(false);
        }
    }, [isStreaming, hasEverStreamed, previewSearchQueries.length]);
    // Map font weight to CSS value
    const fontWeightMap = {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    };
    return (jsxs(Collapsible, Object.assign({ open: isOpen, onOpenChange: handleOpenChange, className: "tw:w-full", style: { marginBottom: `${styles.containerMarginBottom}px` } }, { children: [jsxs(CollapsibleTrigger, Object.assign({ className: "tw:flex tw:items-center tw:cursor-pointer tw:group", style: {
                    gap: `${styles.headerGap}px`,
                    marginBottom: `${styles.headerMarginBottom}px`,
                } }, { children: [jsx(SearchIcon, { size: styles.headerIconSize, color: resolveColor(styles.headerIconColor) }), jsx("span", Object.assign({ style: {
                            fontSize: `${styles.headerLabelFontSize}px`,
                            fontWeight: fontWeightMap[styles.headerLabelFontWeight] || 500,
                            fontFamily: styles.headerLabelFontFamily,
                            color: resolveColor(styles.headerLabelColor),
                        } }, { children: styles.headerLabelText })), jsx(ChevronDown, { style: {
                            width: `${styles.headerChevronSize}px`,
                            height: `${styles.headerChevronSize}px`,
                            color: resolveColor(styles.headerChevronColor),
                            transition: `transform ${styles.chevronRotationDuration}ms`,
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        } })] })), jsx(CollapsibleContent, Object.assign({ forceMount: true, className: cn(!isOpen && 'tw:hidden') }, { children: jsx("div", Object.assign({ className: "tw:flex tw:flex-col tw:items-start", style: {
                        gap: `${styles.pillsGap}px`,
                        marginBottom: `${styles.pillsSectionMarginBottom}px`,
                    } }, { children: previewSearchQueries.map((query, idx) => {
                        var _a, _b;
                        // Find the matching query products for this query
                        const matchingQueryData = previewQueryProducts.find((qp) => qp.query === query);
                        const isLoading = isStreaming && !matchingQueryData;
                        const isAnimating = matchingQueryData ? animatedQueryIndices.has(matchingQueryData.queryIndex) : false;
                        return (jsx(SearchQueryPill, { query: query, products: (_a = matchingQueryData === null || matchingQueryData === void 0 ? void 0 : matchingQueryData.products) !== null && _a !== void 0 ? _a : [], totalResults: (_b = matchingQueryData === null || matchingQueryData === void 0 ? void 0 : matchingQueryData.totalResults) !== null && _b !== void 0 ? _b : 0, isLoading: isLoading, isAnimating: isAnimating }, `${query}-${idx}`));
                    }) })) }))] })));
};
/**
 * Collapsible section displaying search queries with product results.
 * Shows query pills that shimmer while loading and animate product images on arrival.
 * Wrapped with ChainOfThoughtWidget for full customization.
 */
const ProductSearchQueriesSection = ({ previewSearchQueries, previewQueryProducts, isStreaming, currentStatus, hasContent, isLastMessage = false, }) => {
    // Don't render if no queries to show (prevents empty widget wrapper)
    if (previewSearchQueries.length === 0) {
        return null;
    }
    return (jsx(ChainOfThoughtWidget, Object.assign({ widgetId: "chain-of-thought", displayName: "Search Results" }, { children: (styles, resolveColor) => (jsx("div", Object.assign({ style: { width: '100%' } }, { children: jsx(ProductSearchQueriesSectionInner, { previewSearchQueries: previewSearchQueries, previewQueryProducts: previewQueryProducts, isStreaming: isStreaming, currentStatus: currentStatus, hasContent: hasContent, isLastMessage: isLastMessage, styles: styles, resolveColor: resolveColor }) }))) })));
};

export { ProductSearchQueriesSection };
//# sourceMappingURL=ProductSearchQueriesSection.js.map
